import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { analyticsService } from '../services/api';

const StatCard = ({ title, value, subtitle }) => {
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, mt: 1 }}>
          {value}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {subtitle}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
};

const AdminStats = () => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [onboarding, setOnboarding] = useState(null);

  const periodLabel = useMemo(() => `${days} jours`, [days]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [overview, onboardingStats] = await Promise.all([
          analyticsService.getAdminOverview(days),
          analyticsService.getAdminOnboardingActivities(days),
        ]);
        if (cancelled) return;
        setData(overview);
        setOnboarding(onboardingStats);
      } catch (e) {
        if (cancelled) return;
        const message = e?.response?.data?.error || 'Impossible de charger les statistiques (accès admin requis)';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [days]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={900}>
            Statistiques site
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Période : {periodLabel}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {[7, 30, 90].map((d) => (
            <Chip
              key={d}
              label={`${d}j`}
              clickable
              color={days === d ? 'primary' : 'default'}
              onClick={() => setDays(d)}
            />
          ))}
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !data ? (
        <Alert severity="warning">Aucune donnée</Alert>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={`Vues publiques (${periodLabel})`}
                value={data.traffic?.public?.pageViews?.total ?? 0}
                subtitle={`${data.traffic?.public?.pageViews?.uniqueVisitors ?? 0} visiteurs uniques • ${data.traffic?.public?.pageViews?.newVisitors ?? 0} nouveaux`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={`Vues app (${periodLabel})`}
                value={data.traffic?.app?.pageViews?.total ?? 0}
                subtitle={`${data.traffic?.app?.pageViews?.internalAdmin ?? 0} internes (admin) • ${data.traffic?.app?.pageViews?.external ?? 0} externes`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={`Vues admin (${periodLabel})`}
                value={data.traffic?.admin?.pageViews?.total ?? 0}
                subtitle={`${data.traffic?.admin?.pageViews?.internalAdmin ?? 0} internes (admin) • ${data.traffic?.admin?.pageViews?.external ?? 0} externes`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={`Vues totales (${periodLabel})`}
                value={data.pageViews?.total ?? 0}
                subtitle={`${data.users?.total ?? 0} utilisateurs • ${data.users?.new ?? 0} nouveaux`}
              />
            </Grid>
          </Grid>

          {onboarding?.activities?.length ? (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
                      Segments onboarding ({periodLabel})
                    </Typography>
                    <Stack spacing={1}>
                      {onboarding.activities
                        .slice()
                        .sort((a, b) => (b.count || 0) - (a.count || 0))
                        .map((row) => (
                          <Box key={row.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="body2">{row.label}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                              {row.count || 0}
                            </Typography>
                          </Box>
                        ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : null}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Utilisateurs" value={data.users?.total ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title={`Nouveaux utilisateurs (${periodLabel})`} value={data.users?.new ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <StatCard
                title="Abonnements"
                value={`${data.subscriptions?.proActive ?? 0} Pro`}
                subtitle={`${data.subscriptions?.free ?? 0} Free • ${data.subscriptions?.proTrial ?? 0} Trial`}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
                    Top pages (public)
                  </Typography>
                  <Stack spacing={1}>
                    {(data.traffic?.public?.pageViews?.topPages || []).map((p) => (
                      <Box key={p.path} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {p.path}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                          {p.count}
                        </Typography>
                      </Box>
                    ))}
                    {((data.traffic?.public?.pageViews?.topPages?.length ?? 0) === 0) ? (
                      <Typography variant="body2" color="text.secondary">
                        Pas encore de données.
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
                    Vues par jour (public)
                  </Typography>
                  <Stack spacing={1}>
                    {(data.traffic?.public?.pageViews?.byDay || []).slice(-14).map((row) => (
                      <Box key={row.date} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="body2">{row.date}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                          {row.count}
                        </Typography>
                      </Box>
                    ))}
                    {((data.traffic?.public?.pageViews?.byDay?.length ?? 0) === 0) ? (
                      <Typography variant="body2" color="text.secondary">
                        Pas encore de données.
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default AdminStats;
